import React, { useEffect, useState } from 'react';
import { PanelLeft, PanelLeftClose } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const DEFAULT_BOTTOM_PRIMARY = 5;

function sumNavBadges(items) {
  let total = 0;
  let capped = false;
  (items || []).forEach((item) => {
    if (item.badge == null || item.badge === '') return;
    if (item.badge === '99+') {
      capped = true;
      return;
    }
    const n = Number(item.badge);
    if (Number.isFinite(n)) total += n;
  });
  if (capped || total > 99) return '99+';
  return total > 0 ? total : null;
}

/**
 * @param {number} bottomPrimaryCount - Tabs on bottom bar before "More" (ignored when hideBottomNav).
 * @param {boolean} hideBottomNav - All nav in side drawer only (client/owner/operator mobile & tablet).
 * @param {'overflow'|'full'} drawerMode - 'full' = every tab in drawer; 'overflow' = drawer for tabs not on bottom bar.
 * @param {boolean} collapsibleSections - When true, sectionKey groups become accordion headers.
 */
const MobileNav = ({
  navItems,
  activeTab,
  setActiveTab,
  topContent,
  bottomContent,
  title,
  subtitle,
  bottomPrimaryCount = DEFAULT_BOTTOM_PRIMARY,
  /** 'overflow' = drawer only for tabs not on bottom bar; 'full' = drawer lists every tab (admin) */
  drawerMode = 'overflow',
  drawerSectionTitle = 'More',
  hideBottomNav = false,
  collapsibleSections = false,
  navSectionOpen = null,
  onToggleNavSection = null,
  menuOpen: menuOpenProp,
  onMenuOpenChange,
}) => {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const isControlled = menuOpenProp !== undefined;
  const menuOpen = isControlled ? Boolean(menuOpenProp) : internalMenuOpen;
  const setMenuOpen = (next) => {
    const value = typeof next === 'function' ? next(menuOpen) : next;
    if (!isControlled) setInternalMenuOpen(value);
    if (typeof onMenuOpenChange === 'function') onMenuOpenChange(value);
  };
  const items = navItems || [];
  const primaryCount = Math.min(Math.max(1, bottomPrimaryCount), items.length);
  const bottomItems = items.slice(0, primaryCount);
  const overflowItems = items.slice(primaryCount);
  const fullDrawer = hideBottomNav || drawerMode === 'full';
  const drawerItems = fullDrawer ? items : overflowItems;
  const hasDrawer = drawerItems.length > 0;
  const hasOverflow = fullDrawer ? items.length > 0 : overflowItems.length > 0;
  const overflowActive = fullDrawer
    ? items.some((item) => item.id === activeTab)
    : overflowItems.some((item) => item.id === activeTab);

  useEffect(() => {
    setMenuOpen(false);
  // Close on tab change only; setMenuOpen is recreated when controlled.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  const isSectionOpen = (sectionKey) => {
    if (!collapsibleSections || !sectionKey) return true;
    if (navSectionOpen && navSectionOpen[sectionKey]) return true;
    const activeItem = items.find((item) => item.id === activeTab);
    return activeItem?.sectionKey === sectionKey;
  };

  const renderBadge = (badge) => {
    if (badge == null || badge === '') return null;
    return (
      <span className="ml-auto flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-de-accent px-1 text-[10px] font-bold text-white">
        {badge}
      </span>
    );
  };

  const renderDrawerItemButton = (item) => (
    <button
      key={item.id}
      type="button"
      className={`mb-0.5 flex w-full cursor-pointer items-center gap-3 rounded-lg px-3.5 py-3 text-left text-sm ${
        activeTab === item.id
          ? 'border border-de-gold/30 bg-de-gold/10 font-bold text-de-gold'
          : 'border border-transparent bg-transparent text-de-muted'
      }`}
      onClick={() => {
        setActiveTab(item.id);
        closeMenu();
      }}
    >
      <span className="min-w-6 shrink-0 text-lg">{item.icon}</span>
      <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">{item.label}</span>
      {renderBadge(item.badge)}
    </button>
  );

  const renderCollapsibleDrawerItems = () => {
    const nodes = [];
    let index = 0;
    while (index < drawerItems.length) {
      const item = drawerItems[index];
      if (!item.sectionKey) {
        if (index > 0 && drawerItems[index - 1]?.sectionKey) {
          nodes.push(<div key={`section-end-${item.id}`} className="my-1 h-px bg-de-gold/15" />);
        }
        nodes.push(renderDrawerItemButton(item));
        index += 1;
        continue;
      }

      const sectionKey = item.sectionKey;
      const sectionLabel = item.sectionLabel || sectionKey;
      const groupItems = [];
      while (index < drawerItems.length && drawerItems[index].sectionKey === sectionKey) {
        groupItems.push(drawerItems[index]);
        index += 1;
      }
      const open = isSectionOpen(sectionKey);
      const groupBadge = sumNavBadges(groupItems);
      nodes.push(
        <div key={`section-${sectionKey}`} className="mb-0.5">
          <div className="my-1 h-px bg-de-gold/15" />
          <button
            type="button"
            aria-expanded={open}
            className="mb-0.5 flex w-full cursor-pointer items-center gap-2 rounded-lg border border-transparent bg-transparent px-3 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-de-muted"
            onClick={() => {
              if (typeof onToggleNavSection === 'function') onToggleNavSection(sectionKey);
            }}
          >
            <span className="inline-flex w-3 justify-center text-[8px] text-de-gold" aria-hidden>
              {open ? '▼' : '▶'}
            </span>
            <span className="min-w-0 flex-1">{sectionLabel}</span>
            {renderBadge(groupBadge)}
          </button>
          <div
            style={{
              maxHeight: open ? 320 : 0,
              opacity: open ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 300ms ease, opacity 300ms ease',
            }}
          >
            {groupItems.map((groupItem) => renderDrawerItemButton(groupItem))}
          </div>
        </div>,
      );
    }
    return nodes;
  };

  const renderFlatDrawerItems = () => drawerItems.map((item, index) => {
    const prev = drawerItems[index - 1];
    const showSection = Boolean(
      item.sectionLabel
      && item.sectionLabel !== prev?.sectionLabel,
    );
    return (
      <React.Fragment key={item.id}>
        {showSection && (
          <div className="mb-1 mt-2">
            <div className="my-1 h-px bg-de-gold/15" />
            <p className="mb-1 px-2 text-[9px] font-bold uppercase tracking-wider text-de-muted">
              {item.sectionLabel}
            </p>
          </div>
        )}
        {!item.sectionLabel && prev?.sectionLabel ? (
          <div className="my-1 h-px bg-de-gold/15" />
        ) : null}
        {renderDrawerItemButton(item)}
      </React.Fragment>
    );
  });

  const sidebarToggleClass =
    'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-de-gold/35 bg-de-gold/10 text-de-gold transition-colors hover:border-de-gold/55 hover:bg-de-gold/20';

  return (
    <>
      {/* TOP BAR */}
      <div
        className="fixed top-0 left-0 right-0 z-[100] box-border flex max-w-[100vw] items-center justify-between border-b border-de-gold/30 bg-de-panel px-3 pb-2.5 sm:px-4"
        style={{ height: 'calc(56px + env(safe-area-inset-top, 0px))', paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {hasDrawer && hideBottomNav ? (
            <button
              type="button"
              aria-label={menuOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={menuOpen}
              className={sidebarToggleClass}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
            </button>
          ) : null}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-de-logo text-[11px] font-black text-de-navy">
            DE
          </div>
          <div className="min-w-0">
            <p className="m-0 text-xs font-bold leading-snug text-de-gold whitespace-normal">{title || 'Development Express'}</p>
            <p className="m-0 text-[9px] leading-snug text-de-muted whitespace-normal">{subtitle || 'Portal'}</p>
          </div>
        </div>
        <LanguageSelector compact={true} />
        {hasDrawer && !hideBottomNav && (
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="ml-1 shrink-0 cursor-pointer rounded-lg border border-de-gold/30 bg-de-gold/10 px-2.5 py-1.5 text-lg text-de-gold sm:px-3"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        )}
      </div>

      {/* DRAWER — overflow or full admin nav */}
      {hasDrawer && (
        <>
          <div
            className={`de-drawer-backdrop fixed inset-0 z-[200] max-w-[100vw] bg-black/70 ${
              menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
            }`}
            onClick={closeMenu}
            aria-hidden={!menuOpen}
          />
          <div
            className={`de-drawer-panel fixed inset-y-0 left-0 z-[201] flex w-[min(300px,88vw)] max-w-[100vw] flex-col overflow-y-hidden border-r border-de-gold/25 bg-de-drawer shadow-[-8px_0_32px_rgba(0,0,0,0.55)] ${
              menuOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'
            }`}
            onClick={(e) => e.stopPropagation()}
            aria-hidden={!menuOpen}
          >
            <div className="flex shrink-0 items-center gap-2.5 border-b border-de-gold/20 bg-de-gold/5 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-de-logo text-sm font-black text-de-navy">
                DE
              </div>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-[13px] font-bold leading-snug text-de-gold whitespace-normal">{title || 'Development Express'}</p>
                <p className="m-0 text-[10px] leading-snug text-de-muted whitespace-normal">{subtitle || 'Portal'}</p>
              </div>
              <button
                type="button"
                aria-label="Close sidebar"
                className={sidebarToggleClass}
                onClick={closeMenu}
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>

            {topContent && (
              <div className="shrink-0 border-b border-de-gold/15 px-4 py-3">{topContent}</div>
            )}

            <div className="my-1 h-px shrink-0 bg-de-gold/15" />

            <div className="flex-1 overflow-y-auto overflow-x-hidden p-2">
              <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-de-muted">
                {drawerSectionTitle}
              </p>
              {collapsibleSections ? renderCollapsibleDrawerItems() : renderFlatDrawerItems()}
            </div>

            {bottomContent && (
              <div className="shrink-0 border-t border-de-gold/15 px-4 py-3">{bottomContent}</div>
            )}
          </div>
        </>
      )}

      {/* BOTTOM NAV */}
      {!hideBottomNav && (
      <nav className="fixed bottom-0 left-0 right-0 z-[100] flex max-w-[100vw] min-h-[58px] items-center justify-around border-t border-de-gold/30 bg-de-panel px-0.5 py-1.5 pb-[calc(env(safe-area-inset-bottom,0px)+6px)]">
        {bottomItems.map(item => (
          <button
            key={item.id}
            type="button"
            className={`flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-lg border-none bg-transparent px-0.5 py-1 ${
              activeTab === item.id ? 'text-de-gold' : 'text-de-muted'
            }`}
            onClick={() => setActiveTab(item.id)}
          >
            <span className="text-lg leading-none">{item.icon}</span>
            <span className="mt-0.5 w-full whitespace-normal break-words px-0.5 text-center text-[8px] leading-tight sm:text-[9px]" title={item.label}>
              {item.label}
            </span>
          </button>
        ))}
        {hasOverflow && (
          <button
            type="button"
            className={`flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-lg border-none bg-transparent px-0.5 py-1 ${
              overflowActive || menuOpen ? 'text-de-gold' : 'text-de-muted'
            }`}
            onClick={openMenu}
            aria-label="More sections"
          >
            <span className="text-lg leading-none">⋯</span>
            <span className="mt-0.5 w-full whitespace-normal break-words px-0.5 text-center text-[8px] leading-tight sm:text-[9px]">More</span>
          </button>
        )}
      </nav>
      )}
    </>
  );
};

export default MobileNav;
